import React, { useEffect, useRef, useState } from 'react';
import { useSearch } from '../contexts/SearchContext';
import { resolveSearchAction, ENTITY_TYPE_TO_TAB } from '../utils/entityNavigation';
import './SearchModal.css';

export default function SearchModal({ onNavigate, onOpenEntity, canEdit }) {
  const { isSearchOpen, searchQuery, searchResults, isLoading, closeSearch, setSearchQuery } = useSearch();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchResults]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isSearchOpen) return;

      if (e.key === 'Escape') {
        closeSearch();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, searchResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && searchResults.length > 0) {
        e.preventDefault();
        handleResultClick(searchResults[selectedIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen, searchResults, selectedIndex]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && searchResults.length > 0) {
      const selectedElement = resultsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  const handleResultClick = (result) => {
    const action = resolveSearchAction(result, { canEdit });
    if (!action) return;

    // 'entity' goes through CampaignPage's openEntity, which switches to the
    // tab AND opens that row's detail modal. This used to be tab navigation
    // only, which dropped the user in the right list but never opened the
    // thing they searched for.
    //
    // Falling back to the tab when onOpenEntity is absent keeps a click from
    // doing nothing at all if this modal is ever mounted somewhere that can't
    // open entities — a degraded click still beats a dead one.
    if (action.kind === 'entity' && onOpenEntity) {
      onOpenEntity(action.type, action.id);
    } else if (action.kind === 'entity') {
      onNavigate(ENTITY_TYPE_TO_TAB[action.type], null);
    } else {
      onNavigate(action.tab, action.subtab);
    }
    closeSearch();
  };

  const getResultTitle = (result) => {
    switch (result.type) {
      case 'quest':
        return result.data.title;
      case 'location':
        return result.data.name;
      case 'npc':
        return result.data.name;
      case 'item':
        return result.data.name;
      case 'session':
        return result.data.title;
      case 'character':
        return result.data.character_name;
      case 'secret':
        return result.data.name;
      default:
        return 'Unknown';
    }
  };

  const getResultDescription = (result) => {
    const desc = result.data.description || result.data.summary || result.data.personality || '';
    return desc.length > 120 ? desc.substring(0, 120) + '...' : desc;
  };

  const getResultBadge = (result) => {
    switch (result.type) {
      case 'quest':
        return result.data.quest_type || 'Quest';
      case 'location':
        return result.data.location_type || 'Location';
      case 'npc':
        return result.data.title || 'NPC';
      case 'item':
        return result.data.item_type || 'Item';
      case 'session':
        return `Session ${result.data.id}`;
      case 'character':
        return 'My Character';
      case 'secret':
        return result.data.is_mystery ? 'Mystery' : 'Secret';
      default:
        return result.type;
    }
  };

  // Group results by type
  const groupedResults = searchResults.reduce((groups, result, index) => {
    const type = result.type;
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push({ ...result, originalIndex: index });
    return groups;
  }, {});

  const typeLabels = {
    quest: 'Quests',
    location: 'Locations',
    npc: 'NPCs & Factions',
    item: 'Items',
    session: 'Sessions',
    character: 'My Character',
    secret: 'Secrets'
  };

  if (!isSearchOpen) return null;

  return (
    <div className="search-modal-overlay">
      <div className="search-modal">
        <div className="search-modal-header">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder="Search quests, locations, NPCs & factions, items, sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
            />
            {searchQuery && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <div className="search-header-bottom">
            <div className="search-shortcuts">
              <kbd>↑</kbd><kbd>↓</kbd> to navigate
              <kbd>↵</kbd> to select
              <kbd>esc</kbd> to close
            </div>
            <button
              className="search-close-btn"
              onClick={closeSearch}
              title="Close search"
              aria-label="Close search"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="search-modal-body">
          {isLoading ? (
            <div className="search-loading">
              <div className="loading-spinner"></div>
              <p>Loading search data...</p>
            </div>
          ) : searchQuery.trim() === '' ? (
            <div className="search-empty">
              <p>Start typing to search across all campaign content</p>
              <div className="search-tips">
                <h4>Search Tips:</h4>
                <ul>
                  <li>Search by name, description, or type</li>
                  <li>Works across Quests, Locations, NPCs & Factions, Items, Sessions</li>
                  <li>Use keywords like "quest giver", "city", "weapon"</li>
                </ul>
              </div>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="search-no-results">
              <p>No results found for "{searchQuery}"</p>
              <p className="search-suggestion">Try different keywords or check spelling</p>
            </div>
          ) : (
            <div className="search-results" ref={resultsRef}>
              {Object.entries(groupedResults).map(([type, results]) => (
                <div key={type} className="search-group">
                  <h3 className="search-group-title">
                    {results[0].icon} {typeLabels[type]}
                    <span className="search-group-count">{results.length}</span>
                  </h3>
                  {results.map((result) => (
                    <div
                      key={`${result.type}-${result.data.id || 0}`}
                      className={`search-result-item ${result.originalIndex === selectedIndex ? 'selected' : ''}`}
                      onClick={() => handleResultClick(result)}
                      onMouseEnter={() => setSelectedIndex(result.originalIndex)}
                    >
                      <div className="search-result-icon">{result.icon}</div>
                      <div className="search-result-content">
                        <div className="search-result-header">
                          <h4 className="search-result-title">{getResultTitle(result)}</h4>
                          <span className="search-result-badge">{getResultBadge(result)}</span>
                        </div>
                        <p className="search-result-description">{getResultDescription(result)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
